import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { AlertTriangle, HelpCircle, Info, ShieldAlert, X, UserCheck, UserX } from 'lucide-react';

/**
 * ConfirmDialog system
 * ====================
 *
 * A Promise-based replacement for window.confirm() that matches the app's
 * visual style. Wrap the tree in <ConfirmProvider>; then anywhere:
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Delete user?',
 *     message: 'Jane Doe will be removed permanently.',
 *     confirmLabel: 'Delete',
 *     variant: 'danger',           // 'danger' | 'warning' | 'info' | 'question'
 *   });
 *   if (ok) { /* user clicked confirm *\/ }
 *
 * Variants:
 *   - 'danger'   red accent (delete, destructive actions)
 *   - 'warning'  amber accent (non-destructive caution, disable MFA)
 *   - 'info'     teal accent (confirmations that aren't risky)
 *   - 'question' gray accent (default)
 */

const ConfirmContext = createContext(null);

const VARIANTS = {
    danger: {
        icon: AlertTriangle, color: '#DC2626', bg: 'rgba(239,68,68,0.1)',
        gradient: 'linear-gradient(135deg, #DC2626, #B91C1C)',
        ring: 'rgba(220,38,38,0.2)',
    },
    warning: {
        icon: ShieldAlert, color: '#D97706', bg: 'rgba(245,158,11,0.1)',
        gradient: 'linear-gradient(135deg, #D97706, #B45309)',
        ring: 'rgba(217,119,6,0.2)',
    },
    info: {
        icon: Info, color: '#0D9488', bg: 'rgba(13,148,136,0.1)',
        gradient: 'linear-gradient(135deg, #0D9488, #0F766E)',
        ring: 'rgba(13,148,136,0.2)',
    },
    question: {
        icon: HelpCircle, color: '#4B5563', bg: 'rgba(107,114,128,0.1)',
        gradient: 'linear-gradient(135deg, #4B5563, #374151)',
        ring: 'rgba(107,114,128,0.2)',
    },
    approve: {
        icon: UserCheck, 
        color: '#16A34A',
        bg: 'rgba(34,197,94,0.1)',
        gradient: 'linear-gradient(135deg, #22C55E, #16A34A)',
        ring: 'rgba(34,197,94,0.2)',
    },
    reject: {
        icon: UserX, 
        color: '#DC2626',
        bg: 'rgba(239,68,68,0.1)',
        gradient: 'linear-gradient(135deg, #EF4444, #DC2626)',
        ring: 'rgba(220,38,38,0.2)',
    },
};

export const ConfirmProvider = ({ children }) => {
    const [state, setState] = useState(null);
    const resolverRef = useRef(null);

    const confirm = useCallback((options = {}) => {
        return new Promise((resolve) => {
            resolverRef.current = resolve;
            setState({
                title: 'Are you sure?',
                message: '',
                confirmLabel: 'Confirm',
                cancelLabel: 'Cancel',
                variant: 'question',
                ...options,
            });
        });
    }, []);

    const close = useCallback((result) => {
        if (resolverRef.current) {
            resolverRef.current(result);
            resolverRef.current = null;
        }
        setState(null);
    }, []);

    // ESC / Enter keyboard handling
    useEffect(() => {
        if (!state) return;
        const handleKey = (e) => {
            if (e.key === 'Escape') close(false);
            if (e.key === 'Enter') close(true);
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [state, close]);

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {state && <Dialog state={state} onClose={close} />}
        </ConfirmContext.Provider>
    );
};

/**
 * useConfirm — Promise-based confirmation.
 * Returns a function that resolves to `true` (confirm) or `false` (cancel/dismiss).
 *
 * Fallback: if the provider isn't mounted, uses the native window.confirm
 * so callers still work in isolated test harnesses.
 */
export const useConfirm = () => {
    const ctx = useContext(ConfirmContext);
    if (!ctx) {
        return async (opts = {}) =>
            window.confirm([opts.title, opts.message].filter(Boolean).join('\n\n'));
    }
    return ctx;
};

/* ── Dialog UI ──────────────────────────────────────────── */
const Dialog = ({ state, onClose }) => {
    const meta = VARIANTS[state.variant] || VARIANTS.question;
    const Icon = meta.icon;
    const confirmBtnRef = useRef(null);

    const [rejectReason, setRejectReason] = useState('');
    const [rejectReasonError, setRejectReasonError] = useState('');

    // Auto-focus confirm button when opened
    useEffect(() => {
        // Small delay so the animation doesn't steal focus from in-flight events
        const t = setTimeout(() => confirmBtnRef.current?.focus(), 50);
        return () => clearTimeout(t);
    }, []);

    return (
        <div
            name="confirmDialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            style={{
                position: 'fixed', inset: 0, zIndex: 10001,
                background: 'rgba(15, 23, 42, 0.55)',
                backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 20,
                animation: 'confirmFadeIn 0.2s ease-out',
            }}
            onClick={() => onClose(false)}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: '#fff',
                    borderRadius: 18,
                    width: '100%',
                    maxWidth: 460,
                    boxShadow: '0 25px 80px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.05)',
                    overflow: 'hidden',
                    animation: 'confirmSlideUp 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)',
                }}
            >
                {/* Close button */}
                <button
                    onClick={() => onClose(false)}
                    aria-label="Dismiss"
                    style={{
                        position: 'absolute', top: 16, right: 16,
                        width: 32, height: 32, borderRadius: 10,
                        border: 'none', background: 'rgba(255,255,255,0.15)',
                        color: 'rgba(255,255,255,0.85)',
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontFamily: 'inherit', zIndex: 1,
                        transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
                >
                    <X size={14} strokeWidth={2.5} />
                </button>

                {/* Gradient header */}
                <div style={{
                    background: meta.gradient,
                    padding: '28px 32px 24px',
                    textAlign: 'center',
                    color: '#fff',
                    position: 'relative',
                    overflow: 'hidden',
                }}>
                    {/* Subtle bubble decoration */}
                    <div style={{
                        position: 'absolute', top: -40, right: -40,
                        width: 140, height: 140, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.08)',
                        pointerEvents: 'none',
                    }} />
                    <div style={{ position: 'relative', zIndex: 1 }}>
                        <div style={{
                            width: 58, height: 58, borderRadius: 16,
                            background: 'rgba(255,255,255,0.22)',
                            margin: '0 auto 12px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            backdropFilter: 'blur(4px)',
                        }}>
                            <Icon size={28} color="#fff" strokeWidth={2} />
                        </div>
                        <div
                            id="confirm-dialog-title"
                            style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.3px', lineHeight: 1.25 }}
                        >
                            {state.title}
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div style={{ padding: '22px 32px 26px' }}>
                    {state.message && (
                        <p style={{
                            margin: 0, fontSize: 14, color: '#4B5563',
                            lineHeight: 1.6, textAlign: 'center',
                        }}>
                            {state.message}
                        </p>
                    )}

                    {/* Extra warning callout for destructive actions */}
                    {state.variant === 'danger' && state.warning !== false && (
                        <div style={{
                            marginTop: 16,
                            padding: '10px 14px',
                            borderRadius: 10,
                            background: meta.bg,
                            border: `1px solid ${meta.ring}`,
                            fontSize: 12,
                            color: meta.color,
                            textAlign: 'center',
                            fontWeight: 600,
                        }}>
                            {state.warningText || 'This action cannot be undone.'}
                        </div>
                    )}

                    {state.variant === 'reject' && (

                        <div style={{ marginTop: 18 }}>
                            <div style={{
                                marginTop: 16,
                                padding: '10px 14px',
                                borderRadius: 10,
                                background: meta.bg,
                                border: `1px solid ${meta.ring}`,
                                fontSize: 12,
                                color: meta.color,
                                textAlign: 'center',
                                fontWeight: 600,
                            }}>
                                {state.warningText || 'This action cannot be undone.'}
                            </div>
                            <label
                                style={{
                                    display: 'block',
                                    marginBottom: 8,
                                    marginTop: 8,
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: '#374151'
                                }}
                            >
                                Reason to Reject
                            </label>

                            <textarea
                                value={rejectReason}
                                name="txtareaRejectReason"
                                onChange={(e) => {
                                    setRejectReason(e.target.value);

                                    if (e.target.value.trim()) {
                                        setRejectReasonError('');
                                    }
                                }}
                                placeholder="Enter rejection reason..."
                                rows={4}
                                style={{
                                    width: '100%',
                                    resize: 'none',
                                    padding: '10px 12px',
                                    borderRadius: 10,
                                    //border: '1px solid #D1D5DB',
                                    border: `1px solid ${rejectReasonError ? '#DC2626' : '#D1D5DB'}`,
                                    fontSize: 14,
                                    fontFamily: 'inherit',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                            />
                            {rejectReasonError && (
                                <div
                                    style={{
                                        marginTop: 6,
                                        color: '#DC2626',
                                        fontSize: 12,
                                        fontWeight: 500
                                    }}
                                >
                                    {rejectReasonError}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
                        <button
                        name="btnClose"
                            onClick={() => onClose(false)}
                            style={{
                                flex: 1, padding: '11px 16px', borderRadius: 12,
                                border: '1.5px solid #E8ECF0', background: '#fff',
                                color: '#374151', fontWeight: 600, fontSize: 14,
                                cursor: 'pointer', fontFamily: 'inherit',
                                transition: 'all 0.15s',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#F9FAFB';
                                e.currentTarget.style.borderColor = '#D1D5DB';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#fff';
                                e.currentTarget.style.borderColor = '#E8ECF0';
                            }}
                        >
                            {state.cancelLabel}
                        </button>
                        <button
                            name="btnConfirm"
                            ref={confirmBtnRef}
                            //  onClick={() => onClose(true)}
                            onClick={() => {
                                //onClose(
                                //    state.variant === 'reject'
                                //        ? {
                                //            confirmed: true,
                                //            reason: rejectReason
                                //        }
                                //        : true
                                //)

                                if (state.variant === 'reject') {
                                    if (!rejectReason.trim()) {
                                        setRejectReasonError('Reason to Reject is required.');
                                        return;
                                    }

                                    onClose({
                                        confirmed: true,
                                        reason: rejectReason.trim()
                                    });

                                    return;
                                }

                                onClose(true);
                            }
                            }
                            style={{
                                flex: 1, padding: '11px 16px', borderRadius: 12,
                                border: 'none',
                                background: meta.gradient,
                                color: '#fff', fontWeight: 700, fontSize: 14,
                                cursor: 'pointer', fontFamily: 'inherit',
                                boxShadow: `0 4px 12px ${meta.ring}`,
                                transition: 'all 0.15s',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = `0 6px 16px ${meta.ring}`;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = `0 4px 12px ${meta.ring}`;
                            }}
                        >
                            {state.confirmLabel}
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
        @keyframes confirmFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes confirmSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
        </div>
    );
};

export default ConfirmProvider;
